import * as React from "react"
import { Check, ChevronDown } from "lucide-react"
import { type VariantProps } from "class-variance-authority"

import { cn } from "../../lib/utils"
import { Button, buttonVariants } from "./button"
import { ButtonGroup } from "./button-group"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./dropdown-menu"

export interface SplitButtonOption {
  value: string
  label: React.ReactNode
  onSelect: () => void
  disabled?: boolean
}

export interface SplitButtonProps {
  options: SplitButtonOption[]
  defaultValue?: string
  value?: string
  onValueChange?: (value: string) => void
  loading?: boolean
  loadingLabel?: React.ReactNode
  disabled?: boolean
  size?: VariantProps<typeof buttonVariants>["size"]
  variant?: VariantProps<typeof buttonVariants>["variant"]
  className?: string
  menuAlign?: "start" | "center" | "end"
  triggerAriaLabel?: string
}

const Spinner = () => (
  <svg className="animate-spin h-4 w-4 me-2" viewBox="0 0 24 24">
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
      fill="none"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
)

export const SplitButton = ({
  options,
  defaultValue,
  value: controlledValue,
  onValueChange,
  loading = false,
  loadingLabel,
  disabled = false,
  size,
  variant = "default",
  className,
  menuAlign = "end",
  triggerAriaLabel = "More actions",
}: SplitButtonProps) => {
  const [internalValue, setInternalValue] = React.useState<string | undefined>(
    defaultValue ?? options[0]?.value,
  )
  const value = controlledValue ?? internalValue
  const current = options.find((o) => o.value === value) ?? options[0]

  if (!current) return null

  const select = (opt: SplitButtonOption) => {
    if (controlledValue === undefined) setInternalValue(opt.value)
    onValueChange?.(opt.value)
    opt.onSelect()
  }

  return (
    <ButtonGroup className={className}>
      <Button
        type="button"
        variant={variant}
        size={size}
        disabled={disabled || loading || current.disabled}
        onClick={() => current.onSelect()}
      >
        {loading ? (
          <>
            <Spinner />
            {loadingLabel ?? current.label}
          </>
        ) : (
          current.label
        )}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant={variant}
            size={size}
            disabled={disabled || loading || options.length < 2}
            aria-label={triggerAriaLabel}
            className="px-2"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align={menuAlign} className="min-w-[12rem]">
          {options.map((opt) => (
            <DropdownMenuItem
              key={opt.value}
              disabled={opt.disabled}
              onSelect={() => select(opt)}
              className="flex items-center gap-2"
            >
              <Check
                className={cn(
                  "h-4 w-4 shrink-0",
                  opt.value === current.value ? "opacity-100" : "opacity-0",
                )}
              />
              <span>{opt.label}</span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </ButtonGroup>
  )
}
SplitButton.displayName = "SplitButton"
